'use client';

import type { TravelAgencyTour } from '@eliadmin/core/src/api/travel-agency/travel-agency-api';
import { useState, useEffect, useRef } from 'react';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { HtmlRenderer } from '@eliadmin/core/src/components/ui/project-html-renderer';
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { BiCalendar } from 'react-icons/bi';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { formatTourPrice } from '../../../utils/tour-price-utils';

export function TourInfo({ tour }: { tour: TravelAgencyTour }) {
  const { t } = useTranslations();
  const currency = useGlobalStore(state => state.generalLayout.currency);
  const locale = useLocaleStore(state => state.currentLocale);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = descriptionRef.current;
    if (!el) return;
    const { scrollHeight, clientHeight } = el;
    setIsClamped(scrollHeight > clientHeight);
  }, []);

  const handleTabSwitch = (tabValue: string) => {
    window.dispatchEvent(new CustomEvent('setActiveTab', { detail: { tabValue } }));
  };

  /** Pick the single best date = lowest price_with_vat */
  const bestDate = tour.dates.reduce<TravelAgencyTour['dates'][number] | null>((best, d) => {
    if (!best) return d;
    return d.price_with_vat < best.price_with_vat ? d : best;
  }, null);

  const lowestPrice = bestDate?.price_with_vat ?? 0;
  const discountPercent =
    typeof bestDate?.discount === 'number' ? Math.ceil(bestDate.discount) : bestDate?.discount;

  const hasDiscount = typeof discountPercent === 'number' && discountPercent > 0;

  const lowestPriceBeforeDiscount =
    hasDiscount && bestDate?.price_with_vat_before_discount
      ? bestDate.price_with_vat_before_discount
      : null;

  const amenities = tour.attributes.find(attribute => attribute.id === 2);

  return (
    <div className="flex w-full flex-col items-start justify-start gap-6 px-6 md:max-w-[456px] md:px-0 lg:gap-8">
      <h1 data-ea-visual-tag="h2" className="text-2xl font-bold text-foreground sm:text-3xl">{tour.name}</h1>

      <div className="flex flex-col items-start justify-start gap-2">
        <div
          ref={descriptionRef}
          className={cn(
            'ea-tour-info__description',
            isExpanded ? '' : 'line-clamp-4 overflow-hidden',
          )}
        >
          <HtmlRenderer html={tour.short_description} variant="description" />
        </div>

        {isClamped && (
          <button
            data-ea-visual-tag="span"
            type="button"
            aria-expanded={isExpanded}
            className="flex cursor-pointer items-center gap-2 text-sm font-bold text-primary sm:text-base"
            onClick={() => setIsExpanded(v => !v)}
          >
            {isExpanded ? t('text604') : t('text1360')}
            {isExpanded ? <MdKeyboardArrowUp size={20} /> : <MdKeyboardArrowDown size={20} />}
          </button>
        )}
      </div>
      <div className="flex w-full flex-wrap gap-2">
        {amenities?.values.map(v => (
          <span
            key={v.id}
            className="flex items-center gap-2 rounded-[8px] bg-white px-3 py-[6px] text-xs font-light text-foreground sm:text-sm"
          >
            {v.name}
          </span>
        ))}
      </div>
      <div className="flex flex-col items-start gap-2">
        {hasDiscount && (
          <span className="rounded-[6px] bg-accent/15 px-2 py-1.5 text-sm font-bold text-accent">
            {t('text24')} {discountPercent}%
          </span>
        )}

        <div className="flex items-center gap-2">
          <p data-ea-visual-tag="h3" className="text-2xl font-bold text-foreground sm:text-3xl">
            od {formatTourPrice(lowestPrice, locale)} {currency}
          </p>

          {hasDiscount && lowestPriceBeforeDiscount != null && (
            <span className="text-foreground line-through">
              {formatTourPrice(lowestPriceBeforeDiscount, locale)} {currency}
            </span>
          )}
        </div>
      </div>
      <div className="flex w-full flex-col gap-4 sm:w-fit sm:flex-row">
        <Button className="w-full sm:w-fit" onClick={() => handleTabSwitch('terminy-a-ceny')}>
          <BiCalendar size={20} />
          {t('text3972')}
        </Button>

        <Button
          variant="dark"
          className="w-full sm:w-fit"
          onClick={() => handleTabSwitch('program')}
        >
          {t('text3973')}
        </Button>
      </div>
    </div>
  );
}
